import clsx from 'clsx/lite';
import type { LinkProps } from 'next/link';
import Link from 'next/link';

type DefaultProps = Omit<LinkProps, 'href'> &
  React.HTMLProps<HTMLButtonElement> & {
    href?: string;
  };

export function Button({
  className,
  children,
  href,
  target,
  onClick,
  hideMobileIcon,
  hideDesktopIcon,
  isSmall,
  isLarge,
}: DefaultProps & {
  hideMobileIcon?: boolean;
  hideDesktopIcon?: boolean;
  isSmall?: boolean;
  isLarge?: boolean;
}) {
  const Tag = href ? Link : 'button';

  return (
    <Tag
      className={clsx(
        'whitespace-nowrap font-roboto text-[18px] font-medium uppercase leading-[21px] tracking-[.17em]',
        'group flex w-full items-center justify-center gap-6 rounded border border-current px-6',
        'md:max-w-max',
        isSmall ? 'h-[48px]' : isLarge ? 'h-[61px] lg:h-[68px]' : 'h-[61px]',
        className,
      )}
      href={href as string}
      target={target}
      onClick={onClick}
    >
      <span
        className={clsx(
          'relative flex items-center pl-[7px]',
          hideMobileIcon && 'max-md:hidden',
          hideDesktopIcon && 'md:hidden',
        )}
      >
        <Icon />
      </span>
      <span
        className={clsx(
          'transition-transform duration-200',
          !hideMobileIcon && 'max-md:group-hover:translate-x-5',
          !hideDesktopIcon && 'md:group-hover:translate-x-5',
        )}
      >
        {children}
      </span>
    </Tag>
  );
}

export function LinkButton({
  className,
  children,
  href,
  target,
  onClick,
  hideMobileIcon,
  hideDesktopIcon,
}: DefaultProps & {
  hideMobileIcon?: boolean;
  hideDesktopIcon?: boolean;
}) {
  const Tag = href ? Link : 'button';

  return (
    <Tag
      className={clsx(
        'font-roboto text-[18px] font-medium uppercase leading-[21px] tracking-[.17em]',
        'group flex max-w-max items-center gap-6',
        className,
      )}
      href={href as string}
      target={target}
      onClick={onClick}
    >
      <span
        className={clsx(
          'relative flex items-center pl-[7px]',
          hideMobileIcon && 'max-md:hidden',
          hideDesktopIcon && 'md:hidden',
        )}
      >
        <Icon />
      </span>
      <span
        className={clsx(
          'transition-transform duration-200',
          !hideMobileIcon && 'max-md:group-hover:translate-x-5',
          !hideDesktopIcon && 'md:group-hover:translate-x-5',
        )}
      >
        {children}
      </span>
    </Tag>
  );
}

export const ConditionalLink: React.FC<{
  href?: string;
  className?: string;
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}> = ({ href, children, ...props }) => {
  if (href) {
    return (
      <Link href={href} {...props}>
        {children}
      </Link>
    );
  } else {
    return <span {...props}>{children}</span>;
  }
};

export function LoadMoreButton({
  className,
  onClick,
  children,
}: Omit<DefaultProps, 'href'>) {
  return (
    <button
      className={clsx(
        'font-roboto text-[12px] font-bold uppercase leading-[21px] tracking-[1.5px] underline underline-offset-[6px]',
        'md:font-roboto md:text-[18px] md:font-medium md:tracking-[.17em] md:no-underline',
        'group flex h-[48px] w-full items-center justify-center gap-6 rounded border border-current bg-black px-6 text-white',
        'md:h-[68px] md:max-w-max md:bg-white md:text-black',
        className,
      )}
      onClick={onClick}
    >
      <span className="relative hidden items-center pl-[7px] md:flex">
        <Icon />
      </span>
      <span className="transition-transform duration-200 md:group-hover:translate-x-5">
        {children}
      </span>
    </button>
  );
}

function Icon() {
  return (
    <>
      <svg
        fill="none"
        viewBox="0 0 15 15"
        className="absolute h-3.5 w-3.5 -translate-x-1/2 stroke-current stroke-[1.5px] md:stroke-[2px]"
      >
        <path
          d="M7.5 13.396a6 6 0 1 0 0-12 6 6 0 0 0 0 12Z"
          clipRule="evenodd"
        />
      </svg>
      <svg
        fill="none"
        viewBox="0 0 37 15"
        className="h-3 w-[25px] stroke-current stroke-[1.5px] transition-transform duration-200 group-hover:translate-x-5 md:stroke-[2px]"
      >
        <path
          d="M0 7.5h35m0 0L28.6 1 35 7.5 28.6 14"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </>
  );
}
